'use client';

import Link from '@/components/LocaleLink';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Mail, Instagram, Twitter, Facebook, Youtube } from 'lucide-react';
import { useTranslation } from '@/i18n/LanguageProvider';
import AppDownloadButtons from '@/components/AppDownloadButtons';

/** `blogVisible`: ver comentario equivalente en Header.tsx. */
export default function Footer({ blogVisible = true }: { blogVisible?: boolean }) {
  const { t } = useTranslation();
  const pathname = usePathname();

  if (pathname === '/beta') return null;

  return (
    <footer className="bg-gray-900 text-white" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center mb-4">
              <Image src="/logo_iterogo_transparent.png" alt="IteroGO - Audio Tours Autoguiados" width={220} height={56} />
            </div>
            <p className="text-gray-400 text-sm">{t('footer.tagline')}</p>
            <nav aria-label={t('footer.socialNetworks')} className="flex space-x-4 mt-4">
              <a href="https://www.instagram.com/iterogo.tours" className="text-gray-400 hover:text-primary transition-colors" aria-label="Instagram" rel="noopener noreferrer" target="_blank"><Instagram className="w-5 h-5" /></a>
              <a href="https://www.tiktok.com/@iterogo1" className="text-gray-400 hover:text-primary transition-colors" aria-label="TikTok" rel="noopener noreferrer" target="_blank">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.37a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.8z"/></svg>
              </a>
              <a href="https://x.com/IteroGO" className="text-gray-400 hover:text-primary transition-colors" aria-label="Twitter / X" rel="noopener noreferrer" target="_blank"><Twitter className="w-5 h-5" /></a>
              <a href="https://www.facebook.com/iterogo" className="text-gray-400 hover:text-primary transition-colors" aria-label="Facebook" rel="noopener noreferrer" target="_blank"><Facebook className="w-5 h-5" /></a>
              <a href="https://www.youtube.com/@IteroGO" className="text-gray-400 hover:text-primary transition-colors" aria-label="YouTube" rel="noopener noreferrer" target="_blank"><Youtube className="w-5 h-5" /></a>
            </nav>
            <div className="mt-6">
              <AppDownloadButtons className="!items-start" />
            </div>
          </div>

          <nav aria-label={t('footer.explore')}>
            <h3 className="font-semibold text-lg mb-4">{t('footer.explore')}</h3>
            <ul className="space-y-2">
              <li><Link href="/tours" className="text-gray-400 hover:text-primary transition-colors">{t('footer.allTours')}</Link></li>
              <li><Link href="/guias" className="text-gray-400 hover:text-primary transition-colors">{t('footer.becomeGuide')}</Link></li>
              {blogVisible && <li><Link href="/blog" className="text-gray-400 hover:text-primary transition-colors">{t('footer.blog')}</Link></li>}
              <li><Link href="/about" className="text-gray-400 hover:text-primary transition-colors">{t('footer.aboutUs')}</Link></li>
              <li><Link href="/faq" className="text-gray-400 hover:text-primary transition-colors">{t('footer.frequentQuestions')}</Link></li>
            </ul>
          </nav>

          <nav aria-label={t('footer.support')}>
            <h3 className="font-semibold text-lg mb-4">{t('footer.support')}</h3>
            <ul className="space-y-2">
              <li><Link href="/support" className="text-gray-400 hover:text-primary transition-colors">{t('footer.helpCenter')}</Link></li>
              <li><Link href="/contact" className="text-gray-400 hover:text-primary transition-colors">{t('footer.contact')}</Link></li>
              <li><Link href="/sugerencias" className="text-gray-400 hover:text-primary transition-colors">{t('suggestions.title')}</Link></li>
              <li><Link href="/delete-account" className="text-gray-400 hover:text-primary transition-colors">{t('footer.deleteAccount')}</Link></li>
            </ul>
          </nav>

          <div>
            <h3 className="font-semibold text-lg mb-4">{t('footer.contactTitle')}</h3>
            <ul className="space-y-2">
              <li className="flex items-center text-gray-400"><Mail className="w-4 h-4 mr-2 flex-shrink-0" />hola@iterogo.com</li>
            </ul>
          </div>
        </div>

        <hr className="border-gray-800 my-8" />
        <div className="flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} IteroGO LLC. {t('footer.rights')}</p>
          <nav aria-label={t('footer.legal')} className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 md:mt-0">
            <Link href="/privacy" className="hover:text-primary transition-colors">{t('footer.privacy')}</Link>
            <Link href="/terms" className="hover:text-primary transition-colors">{t('footer.terms')}</Link>
            <Link href="/cookies" className="hover:text-primary transition-colors">{t('footer.cookies')}</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
